/* ===========================================================
   PRODUCTS LISTING — filters, sort, view-toggle, pagination
   =========================================================== */
import React from "react";
import { ProductCard, STOCK_PILL } from "@/components/product-card";
import { Breadcrumbs, PageHeader, getQuery } from "@/components/shell";
import { NETHRA_DATA } from "@/data/data";
import { Icons } from "@/lib/icons";
import { useProducts } from "@/api/hooks";

const { useState: plUS, useEffect: plUE, useMemo: plUM } = React;

function FilterCheck({ checked, onChange, label, count }) {
  return (
    <label className="filter-check">
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span className="filter-check-box"><Icons.Check size={10} stroke={3} /></span>
      <span>{label}</span>
      {count != null && <span className="filter-check-count mono">{count.toLocaleString("en-IN")}</span>}
    </label>
  );
}

function Filters({ filters, setFilters, counts, onClear, isMobileOpen, onCloseMobile }) {
  const cats = NETHRA_DATA.categories;
  const brands = NETHRA_DATA.brands;
  const toggle = (key, value) => {
    setFilters((f) => {
      const cur = new Set(f[key]);
      cur.has(value) ? cur.delete(value) : cur.add(value);
      return { ...f, [key]: cur };
    });
  };
  const setSingle = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  return (
    <aside className={"filters" + (isMobileOpen ? " is-mobile-open" : "")}>
      {isMobileOpen && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ fontSize: 18 }}>Filters</h3>
          <button className="icon-btn" onClick={onCloseMobile} aria-label="Close"><Icons.X size={18} /></button>
        </div>
      )}

      <div className="filters-card">
        <div className="filters-card-head">
          <div className="filters-card-title">Category</div>
        </div>
        <div className="filter-group">
          {cats.map((c) => (
            <FilterCheck
              key={c.id}
              checked={filters.categories.has(c.slug)}
              onChange={() => toggle("categories", c.slug)}
              label={c.name}
              count={counts.byCategory[c.slug] || 0}
            />
          ))}
        </div>
      </div>

      <div className="filters-card">
        <div className="filters-card-head">
          <div className="filters-card-title">Brand</div>
        </div>
        <div className="filter-group" style={{ maxHeight: 240, overflowY: "auto" }}>
          {brands.map((b) => (
            <FilterCheck
              key={b}
              checked={filters.brands.has(b)}
              onChange={() => toggle("brands", b)}
              label={b}
              count={counts.byBrand[b] || 0}
            />
          ))}
        </div>
      </div>

      <div className="filters-card">
        <div className="filters-card-head"><div className="filters-card-title">Price (₹)</div></div>
        <div className="filter-range">
          <input className="filter-range-input" type="number" placeholder="Min" value={filters.priceMin || ""} onChange={(e) => setSingle("priceMin", e.target.value)} />
          <span style={{ color: "var(--ink-3)" }}>—</span>
          <input className="filter-range-input" type="number" placeholder="Max" value={filters.priceMax || ""} onChange={(e) => setSingle("priceMax", e.target.value)} />
        </div>
      </div>

      <div className="filters-card">
        <div className="filters-card-head"><div className="filters-card-title">Stock status</div></div>
        <div className="filter-radio-row">
          {[{ k: "all", l: "All" }, { k: "in_stock", l: "In stock" }, { k: "low_stock", l: "Low stock" }].map((s) => (
            <button key={s.k} className={"filter-radio" + (filters.stock === s.k ? " is-active" : "")} onClick={() => setSingle("stock", s.k)}>{s.l}</button>
          ))}
        </div>
      </div>

      <div className="filters-card">
        <div className="filters-card-head"><div className="filters-card-title">Rating</div></div>
        <div className="filter-radio-row">
          {[0, 4, 4.5].map((r) => (
            <button key={r} className={"filter-radio" + (filters.minRating === r ? " is-active" : "")} onClick={() => setSingle("minRating", r)}>
              {r === 0 ? "Any" : r.toFixed(1) + " ★ & up"}
            </button>
          ))}
        </div>
      </div>

      <div className="filters-card">
        <div className="filters-card-head"><div className="filters-card-title">Schedule</div></div>
        <div className="filter-radio-row">
          {[{ k: "any", l: "Any" }, { k: "H", l: "Schedule H" }, { k: "OTC", l: "OTC" }].map((s) => (
            <button key={s.k} className={"filter-radio" + (filters.schedule === s.k ? " is-active" : "")} onClick={() => setSingle("schedule", s.k)}>{s.l}</button>
          ))}
        </div>
      </div>

      <button className="btn btn-outline" style={{ width: "100%", justifyContent: "center" }} onClick={onClear}>Clear all filters</button>
    </aside>
  );
}

function ProductsPage() {
  // initial category filter from URL (?slug=cardio)
  const slugQ = getQuery("slug") || getQuery("category");
  const [filters, setFilters] = plUS({
    categories: new Set(slugQ ? [slugQ] : []),
    brands: new Set(),
    priceMin: "",
    priceMax: "",
    stock: "all",
    minRating: 0,
    schedule: "any",
  });
  const [sort, setSort] = plUS("relevance");
  const [view, setView] = plUS("grid");
  const [mobileFiltersOpen, setMobileFiltersOpen] = plUS(false);
  const [page, setPage] = plUS(1);
  const perPage = 12;

  // ---- Server-side filter/sort/paginate via /api/v1/products --------------
  // Phase 2 sends category/brand/sub_category/schedule/in_stock/featured/sort
  // to the backend. priceMin/priceMax/minRating remain client-side (see plan).
  const apiCategory = filters.categories.size === 1
    ? Array.from(filters.categories)[0]
    : undefined;  // multi-category isn't supported by the API yet — fall through to client-side
  const apiBrand = filters.brands.size > 0
    ? Array.from(filters.brands).join(",")
    : undefined;
  const apiInStock = filters.stock === "in_stock"
    ? true
    : filters.stock === "out_of_stock" || filters.stock === "low_stock"
    ? false
    : undefined;
  const apiSchedule = filters.schedule !== "any" ? filters.schedule : undefined;

  const queryParams = {
    category: apiCategory,
    brand: apiBrand,
    in_stock: apiInStock,
    schedule: apiSchedule,
    sort,
    limit: perPage,
    offset: (page - 1) * perPage,
  };
  const { data: pageData, isLoading, isError } = useProducts(queryParams);

  // Client-side residual filtering for price/rating + when multi-category is set.
  const apiItems = pageData?.items ?? [];
  const filtered = plUM(() => {
    return apiItems.filter((p) => {
      if (filters.categories.size > 1 && !filters.categories.has(p.category_slug)) return false;
      if (filters.priceMin && p.price_min < +filters.priceMin) return false;
      if (filters.priceMax && p.price_max > +filters.priceMax) return false;
      if (filters.minRating > 0 && p.rating < filters.minRating) return false;
      return true;
    });
  }, [apiItems, filters.categories, filters.priceMin, filters.priceMax, filters.minRating]);

  const sorted = filtered;  // server already sorted
  const paginated = sorted;
  const totalServer = pageData?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalServer / perPage));
  plUE(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  // Counts for sidebar facets — keep client-side derivation from the current
  // page until the API exposes a /products/facets endpoint (Phase 3).
  const counts = plUM(() => {
    const byCat = {}, byBrand = {};
    apiItems.forEach((p) => {
      byCat[p.category_slug] = (byCat[p.category_slug] || 0) + 1;
      byBrand[p.brand] = (byBrand[p.brand] || 0) + 1;
    });
    return { byCategory: byCat, byBrand: byBrand };
  }, [apiItems]);

  const clearAll = () => setFilters({
    categories: new Set(), brands: new Set(), priceMin: "", priceMax: "",
    stock: "all", minRating: 0, schedule: "any",
  });

  // active filter chips
  const chips = [];
  filters.categories.forEach((s) => {
    const cat = NETHRA_DATA.categories.find((c) => c.slug === s);
    chips.push({ k: "categories-" + s, label: cat ? cat.name : s, onRemove: () => setFilters((f) => { const n = new Set(f.categories); n.delete(s); return { ...f, categories: n }; }) });
  });
  filters.brands.forEach((b) => {
    chips.push({ k: "brand-" + b, label: b, onRemove: () => setFilters((f) => { const n = new Set(f.brands); n.delete(b); return { ...f, brands: n }; }) });
  });
  if (filters.stock !== "all") chips.push({ k: "stock", label: STOCK_PILL[filters.stock]?.label || filters.stock, onRemove: () => setFilters((f) => ({ ...f, stock: "all" })) });
  if (filters.minRating > 0) chips.push({ k: "rating", label: filters.minRating + " ★ & up", onRemove: () => setFilters((f) => ({ ...f, minRating: 0 })) });
  if (filters.schedule !== "any") chips.push({ k: "sched", label: "Schedule " + filters.schedule, onRemove: () => setFilters((f) => ({ ...f, schedule: "any" })) });
  if (filters.priceMin) chips.push({ k: "pmin", label: "≥ ₹" + filters.priceMin, onRemove: () => setFilters((f) => ({ ...f, priceMin: "" })) });
  if (filters.priceMax) chips.push({ k: "pmax", label: "≤ ₹" + filters.priceMax, onRemove: () => setFilters((f) => ({ ...f, priceMax: "" })) });

  // current category info for header
  const onlyCat = (filters.categories.size === 1) ? NETHRA_DATA.categories.find((c) => filters.categories.has(c.slug)) : null;

  return (
    <>
      <Breadcrumbs items={[{ label: "Products" }]} />
      <PageHeader
        eyebrow={"Catalog · " + totalServer.toLocaleString("en-IN") + " SKUs"}
        title={onlyCat ? onlyCat.name : "All products"}
        description={onlyCat ? onlyCat.description : "Search, filter and shop across our complete catalog of 12,400+ healthcare SKUs. Same-day dispatch on orders before 4 PM IST."}
        image="https://siddham.in/wp-content/uploads/monsoon-treatment.jpg"
        stats={[
          { v: "12,400+", k: "Active SKUs" },
          { v: "50",      k: "Cities served" },
          { v: "99.4%",   k: "On-time dispatch" },
        ]}
      />

      <div className="container">
        <div className="products-layout">
          <Filters
            filters={filters}
            setFilters={setFilters}
            counts={counts}
            onClear={clearAll}
            isMobileOpen={mobileFiltersOpen}
            onCloseMobile={() => setMobileFiltersOpen(false)}
          />

          <div className="products-main">
            <div className="products-toolbar">
              <div className="products-count">
                {isLoading
                  ? <>Loading products…</>
                  : isError
                  ? <>Couldn't load catalog — <button className="link-button" onClick={() => window.location.reload()}>retry</button></>
                  : <>Showing <strong>{paginated.length}</strong> of <strong>{totalServer.toLocaleString("en-IN")}</strong> products</>
                }
              </div>
              <div className="toolbar-right">
                <button className="mobile-filter-btn" onClick={() => setMobileFiltersOpen(true)}>
                  <Icons.Menu size={14} /> Filters
                  {chips.length > 0 && <span className="mobile-filter-btn-count">{chips.length}</span>}
                </button>
                <select className="sort-select" value={sort} onChange={(e) => setSort(e.target.value)}>
                  <option value="relevance">Sort: Relevance</option>
                  <option value="popular">Most popular</option>
                  <option value="rating">Highest rated</option>
                  <option value="price-asc">Price: low to high</option>
                  <option value="price-desc">Price: high to low</option>
                </select>
                <div className="view-toggle">
                  <button className={view === "grid" ? "is-active" : ""} onClick={() => setView("grid")} aria-label="Grid view"><Icons.Grid size={14} /></button>
                  <button className={view === "list" ? "is-active" : ""} onClick={() => setView("list")} aria-label="List view"><Icons.Menu size={14} /></button>
                </div>
              </div>
            </div>

            {chips.length > 0 && (
              <div className="active-filters">
                {chips.map((c) => (
                  <span key={c.k} className="active-filter">
                    {c.label}
                    <button onClick={c.onRemove} aria-label="Remove"><Icons.X size={11} stroke={2.2} /></button>
                  </span>
                ))}
                <button className="active-filter" style={{ color: "var(--rose)" }} onClick={clearAll}>Clear all</button>
              </div>
            )}

            {paginated.length === 0 ? (
              <div className="empty">
                <div className="empty-ic"><Icons.Search size={26} stroke={1.6} /></div>
                <div className="empty-title">No products match your filters</div>
                <div className="empty-desc">Try widening the price range, removing a brand, or clearing all filters to see the full catalog.</div>
                <div className="empty-actions">
                  <button className="btn btn-primary" onClick={clearAll}>Clear all filters</button>
                </div>
              </div>
            ) : view === "grid" ? (
              <div className="prod-grid cols-4">
                {paginated.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
            ) : (
              <div className="prod-list-view">
                {paginated.map((p) => <ProductCard key={p.id} product={p} layout="list" />)}
              </div>
            )}

            {totalPages > 1 && (
              <div className="pagination">
                <button className="page-btn" disabled={page === 1} onClick={() => setPage(page - 1)}><Icons.ChevL size={14} /></button>
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button key={i} className={"page-btn" + (page === i + 1 ? " is-active" : "")} onClick={() => setPage(i + 1)}>{i + 1}</button>
                ))}
                <button className="page-btn" disabled={page === totalPages} onClick={() => setPage(page + 1)}><Icons.ChevR size={14} /></button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export { ProductsPage };
